(function () {
  if (window.KoldeSingleItemCart || !window.fetch) {
    return;
  }

  const nativeFetch = window.fetch.bind(window);
  const NativeXMLHttpRequest = window.XMLHttpRequest;
  const nativeFormSubmit = HTMLFormElement.prototype.submit;
  const nativeFormRequestSubmit = HTMLFormElement.prototype.requestSubmit;
  let isNormalizing = false;

  const getCartUrl = () =>
    window.themeVariables?.routes?.cartUrl || window.routes?.cart_url || "/cart";

  const getCartAddUrl = () =>
    window.themeVariables?.routes?.cartAddUrl || window.routes?.cart_add_url || "/cart/add";

  const getCartAddJsonUrl = () => {
    const cartAddUrl = getCartAddUrl();
    return cartAddUrl.endsWith(".js") ? cartAddUrl : `${cartAddUrl}.js`;
  };

  const getCartJsonUrl = () => `${getCartUrl().replace(/\/$/, "")}.js`;
  const getCartUpdateUrl = () => `${getCartUrl().replace(/\/$/, "")}/update.js`;

  const getUrlPath = (url) => {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, "");
    } catch {
      return "";
    }
  };

  const getRequestUrl = (input) => {
    if (typeof input === "string" || input instanceof URL) {
      return input.toString();
    }

    if (typeof Request !== "undefined" && input instanceof Request) {
      return input.url;
    }

    return "";
  };

  const getHeaderValue = (headers, name) => {
    if (!headers) {
      return null;
    }

    if (typeof Headers !== "undefined" && headers instanceof Headers) {
      return headers.get(name);
    }

    const lowerName = name.toLowerCase();
    if (Array.isArray(headers)) {
      const header = headers.find(([key]) => key.toLowerCase() === lowerName);
      return header ? header[1] : null;
    }

    return headers[name] || headers[lowerName] || null;
  };

  const removeHeader = (headers, name) => {
    if (!headers) {
      return headers;
    }

    if (typeof Headers !== "undefined" && headers instanceof Headers) {
      const nextHeaders = new Headers(headers);
      nextHeaders.delete(name);
      return nextHeaders;
    }

    if (Array.isArray(headers)) {
      const lowerName = name.toLowerCase();
      return headers.filter(([key]) => key.toLowerCase() !== lowerName);
    }

    const nextHeaders = { ...headers };
    delete nextHeaders[name];
    delete nextHeaders[name.toLowerCase()];
    return nextHeaders;
  };

  const isIzyrentBridgeRequest = (input, init) => {
    const headers =
      init?.headers ||
      (typeof Request !== "undefined" && input instanceof Request
        ? input.headers
        : null);

    return getHeaderValue(headers, "X-Kolde-Izyrent-Bridge") === "1";
  };

  const isCartAddUrl = (url) => /\/cart\/add(?:\.js)?$/.test(getUrlPath(url));

  // IzyRent (the rental app) owns rental add-to-cart end to end: it runs its own
  // document submit handler and reserves a server-side validation token, and all
  // of its cart requests are tagged with "izyrent" in the URL. This single-item
  // helper must NOT also handle those forms/requests — doing so double-adds a
  // token-less line and races IzyRent, which made the cart drawer flash open then
  // vanish (and sometimes not open at all). Skip anything IzyRent drives.
  const isIzyrentRequest = (url) => /izyrent/i.test(String(url || ""));

  const isIzyrentForm = (form) =>
    !!form &&
    typeof form.querySelector === "function" &&
    !!form.querySelector(".izyAtc, [data-izy-add], [data-izy-variant]");

  const isPostRequest = (input, init) => {
    const method =
      init?.method ||
      (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET");

    return method.toUpperCase() === "POST";
  };

  const setJsonQuantityToOne = (value) => {
    if (!value || typeof value !== "object") {
      return value;
    }

    if (Array.isArray(value.items)) {
      value.items = value.items.map((item) => ({
        ...item,
        quantity: 1,
      }));
    }

    if ("quantity" in value) {
      value.quantity = 1;
    }

    return value;
  };

  const prepareAddBody = (body, includeSections = false) => {
    if (!body) {
      return body;
    }

    if (typeof FormData !== "undefined" && body instanceof FormData) {
      body.set("quantity", "1");
      if (includeSections) {
        body.set("sections", "mini-cart");
        body.set("sections_url", window.location.pathname);
      }
      return body;
    }

    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      body.set("quantity", "1");
      if (includeSections) {
        body.set("sections", "mini-cart");
        body.set("sections_url", window.location.pathname);
      }
      return body;
    }

    if (typeof body !== "string") {
      return body;
    }

    const trimmedBody = body.trim();
    if (!trimmedBody) {
      return body;
    }

    if (trimmedBody[0] === "{" || trimmedBody[0] === "[") {
      try {
        const jsonBody = setJsonQuantityToOne(JSON.parse(trimmedBody));
        if (includeSections && jsonBody && typeof jsonBody === "object") {
          jsonBody.sections = ["mini-cart"];
          jsonBody.sections_url = window.location.pathname;
        }
        return JSON.stringify(jsonBody);
      } catch {
        return body;
      }
    }

    try {
      const params = new URLSearchParams(body);
      if (params.has("id") || params.has("quantity")) {
        params.set("quantity", "1");
        if (includeSections) {
          params.set("sections", "mini-cart");
          params.set("sections_url", window.location.pathname);
        }
        return params.toString();
      }
    } catch {
      return body;
    }

    return body;
  };

  const getCart = async () => {
    const response = await nativeFetch(getCartJsonUrl(), {
      credentials: "same-origin",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  };

  const getCartSnapshot = () => getCart().catch(() => null);

  const getAddedItems = (addResponse) => {
    if (Array.isArray(addResponse?.items)) {
      return addResponse.items;
    }

    return addResponse ? [addResponse] : [];
  };

  const getPreferredItem = (items, preferredKey) =>
    items.find((item) => item.key === preferredKey) || items[items.length - 1] || null;

  const updateCartToSingleItem = async (items, preferredKey) => {
    const preferredItem = getPreferredItem(items, preferredKey);
    if (!preferredItem?.key) {
      return null;
    }

    const updates = {};
    items.forEach((item) => {
      if (item?.key) {
        updates[item.key] = item.key === preferredItem.key ? 1 : 0;
      }
    });

    if (!Object.keys(updates).length) {
      return null;
    }

    const updateResponse = await nativeFetch(getCartUpdateUrl(), {
      body: JSON.stringify({
        updates,
        sections: ["mini-cart"],
        sections_url: window.location.pathname,
      }),
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    return updateResponse.ok ? updateResponse : null;
  };

  const normalizeAddedResponse = async (response, cartBefore) => {
    if (!response.ok || isNormalizing) {
      return response;
    }

    let addResponse = null;
    try {
      addResponse = await response.clone().json();
    } catch {
      return response;
    }

    const addedItems = getAddedItems(addResponse);
    const addedItem = addedItems[addedItems.length - 1] || null;
    const hasMiniCartSection = Boolean(addResponse?.sections?.["mini-cart"]);
    const hadExistingItems = Boolean(cartBefore?.items?.length);
    const addedMultipleItems = addedItems.length > 1;
    const needsQuantityFix = addedItems.some((item) => item?.quantity !== 1);
    const needsFinalSection = !hasMiniCartSection;

    if (!hadExistingItems && !addedMultipleItems && !needsQuantityFix && !needsFinalSection) {
      return response;
    }

    isNormalizing = true;

    try {
      let itemsForUpdate = cartBefore?.items || [];

      if (!itemsForUpdate.length || addedMultipleItems) {
        const cartAfterAdd = await getCart();
        itemsForUpdate = cartAfterAdd?.items || itemsForUpdate;
      } else if (addedItem?.key && !itemsForUpdate.some((item) => item.key === addedItem.key)) {
        itemsForUpdate = [...itemsForUpdate, addedItem];
      }

      const normalizedResponse = await updateCartToSingleItem(
        itemsForUpdate,
        addedItem?.key
      );

      return normalizedResponse || response;
    } finally {
      isNormalizing = false;
    }
  };

  const dispatchCartRefresh = async (response, openMiniCart = true) => {
    let cart = null;
    try {
      cart = await response.clone().json();
    } catch {
      return;
    }

    if (typeof cart?.item_count !== "number") {
      cart = await getCart();
    }

    if (typeof cart?.item_count === "number") {
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { cart },
        })
      );
    }

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          cart,
          openMiniCart,
        },
      })
    );
  };

  const prepareFetchArgs = async (input, init) => {
    const nextInit = init ? { ...init } : {};

    if (nextInit.body) {
      nextInit.body = prepareAddBody(nextInit.body, true);
      return [input, nextInit];
    }

    if (typeof Request !== "undefined" && input instanceof Request) {
      nextInit.method = nextInit.method || input.method;
      nextInit.headers = nextInit.headers || input.headers;

      try {
        nextInit.body = prepareAddBody(await input.clone().formData(), true);
      } catch {
        try {
          nextInit.body = prepareAddBody(await input.clone().text(), true);
        } catch {
          // Leave the request body untouched if the browser will not let us clone it.
        }
      }

      return [input.url, nextInit];
    }

    return [input, nextInit];
  };

  const addToCart = async (body, init = {}) => {
    const cartBeforePromise = getCartSnapshot();
    const response = await nativeFetch(getCartAddJsonUrl(), {
      ...init,
      body: prepareAddBody(body, true),
      credentials: "same-origin",
      method: "POST",
    });
    const cartBefore = await cartBeforePromise;

    return normalizeAddedResponse(response, cartBefore);
  };

  window.KoldeSingleItemCart = {
    addToCart,
    nativeFetch,
    normalizeAddedResponse,
    prepareAddBody,
  };

  window.fetch = async function singleItemCartFetch(input, init) {
    const requestUrl = getRequestUrl(input);
    if (!isPostRequest(input, init) || !isCartAddUrl(requestUrl)) {
      return nativeFetch(input, init);
    }

    if (isIzyrentRequest(requestUrl)) {
      return nativeFetch(input, init);
    }

    const isIzyrentBridge = isIzyrentBridgeRequest(input, init);
    const [nextInput, nextInit] = await prepareFetchArgs(input, init);
    if (isIzyrentBridge) {
      nextInit.headers = removeHeader(
        nextInit.headers,
        "X-Kolde-Izyrent-Bridge"
      );
      return nativeFetch(nextInput, nextInit);
    }

    const cartBeforePromise = getCartSnapshot();
    const response = await nativeFetch(nextInput, nextInit);
    const cartBefore = await cartBeforePromise;

    return normalizeAddedResponse(response, cartBefore);
  };

  if (NativeXMLHttpRequest) {
    const nativeOpen = NativeXMLHttpRequest.prototype.open;
    const nativeSend = NativeXMLHttpRequest.prototype.send;

    NativeXMLHttpRequest.prototype.open = function patchedOpen(method, url) {
      this.koldeCartRequest = {
        method: method ? method.toUpperCase() : "GET",
        url,
      };

      return nativeOpen.apply(this, arguments);
    };

    NativeXMLHttpRequest.prototype.send = function patchedSend(body) {
      if (
        this.koldeCartRequest?.method === "POST" &&
        isCartAddUrl(this.koldeCartRequest.url) &&
        !isIzyrentRequest(this.koldeCartRequest.url)
      ) {
        const cartBeforePromise = getCartSnapshot();
        this.addEventListener(
          "loadend",
          async () => {
            try {
              const cartBefore = await cartBeforePromise;
              const addResponse = new Response(this.responseText, {
                status: this.status,
                statusText: this.statusText,
              });
              await normalizeAddedResponse(addResponse, cartBefore);
            } catch (error) {
              console.error(error);
            }
          },
          { once: true }
        );

        nativeSend.call(this, prepareAddBody(body, true));
        return;
      }

      return nativeSend.apply(this, arguments);
    };
  }

  const handleCartAddForm = (form) => {
    addToCart(new FormData(form), {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => dispatchCartRefresh(response, true))
      .catch((error) => console.error(error));
  };

  HTMLFormElement.prototype.submit = function patchedSubmit() {
    if (isIzyrentForm(this)) {
      return;
    }
    if (isCartAddUrl(this.action) && this.method.toUpperCase() === "POST") {
      handleCartAddForm(this);
      return;
    }

    return nativeFormSubmit.call(this);
  };

  if (nativeFormRequestSubmit) {
    HTMLFormElement.prototype.requestSubmit = function patchedRequestSubmit(submitter) {
      if (isIzyrentForm(this)) {
        return;
      }
      if (isCartAddUrl(this.action) && this.method.toUpperCase() === "POST") {
        if (!this.checkValidity()) {
          this.reportValidity();
          return;
        }

        handleCartAddForm(this);
        return;
      }

      return nativeFormRequestSubmit.call(this, submitter);
    };
  }

  document.addEventListener("submit", (event) => {
    if (event.defaultPrevented || !(event.target instanceof HTMLFormElement)) {
      return;
    }

    const form = event.target;
    if (!isCartAddUrl(form.action) || form.method.toUpperCase() !== "POST") {
      return;
    }

    if (isIzyrentForm(form)) {
      // IzyRent's own submit handler adds this form (with the rental token) and
      // cancels the native POST. Cancel it here too so nothing ever navigates,
      // but do NOT add — that would create a duplicate, token-less line.
      event.preventDefault();
      return;
    }

    event.preventDefault();

    handleCartAddForm(form);
  });
})();
